/**
 * Área de trabalho da sala (TfrmRoomMainDesktop / TfrmBaseRoomDesktopForm).
 *
 * - Sem barra no alto (docs/spec/room-menus.md): o menu da mesa (convidar, configurações, o
 *   submenu Abas, que abre e fecha as abas, organiza a tela e abre uma janela nova, e sair) abre
 *   pelo botão direito no ícone da mesa, na barra da esquerda (`roomMenu.ts`); a conferência fica na
 *   aba Participantes. O original tem no alto a faixa de abas da área de trabalho ("Mesa" e "+") e o
 *   menu da mesa repetido no canto.
 * - A mesa é só de abas, que se encaixam em espaços (docs/spec/room-layout.md), desenhados por
 *   `room/Desk.tsx`, como nas janelas próprias. Uma aba nova entra num espaço que já existe
 *   (`tabDestination`), e as abas e os espaços se arrastam a qualquer hora.
 * - Ctrl+E (ou Abas → Organizar a tela) liga o modo de edição: o "+" põe uma aba num lugar, na
 *   borda de cada espaço e da mesa.
 * - Esta é a janela principal: só ela grava o layout, e as janelas próprias (docs/spec/popout-windows.md)
 *   mandam para cá o que mudam nele e o que pedem.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '../i18n/index.js';
import { useApp } from '../store.js';
import { ConferenceAudio } from './voice/ConferencePanel.js';
import { useVoiceChat } from '../voice/useVoiceChat.js';
import { VideoContext, videoOf } from '../voice/videoContext.js';
import { VoiceContext } from '../voice/voiceContext.js';
import { provideRoomMenu } from './roomMenu.js';
import { Icon } from '../ui/icons.js';
import {
  activateSheet,
  applyLayoutOp,
  attachWindow,
  closeParamSheets,
  closeSheet,
  findSheet,
  findTab,
  chatPanel,
  isActiveSheet,
  itemModule,
  loadLayout,
  newLayoutId,
  newWindow,
  openTab,
  popTab,
  resetLayout,
  saveLayout,
  tabDestination,
  visibleLayout,
  windowIds,
  windowOf,
  windowOfNode,
  windowRoot,
  withoutModule,
  type DesktopLayout,
  type DockNode,
  type LayoutOp,
  type ModuleClassId,
  type OpenOpts,
} from './layout.js';
import { askConfirm } from '../ui/prompt.js';
import { closedPopouts, focusPopout, onWindowMessage, openPopout, popoutClosed, postWindows } from './windows.js';
import { toast } from '../ui/Toaster.js';
import { type MenuEntry } from '../ui/ContextMenu.js';
import { isBuiltinTable, sessionRules } from '../shared.js';
import { ChooseUsersDialog } from './modules/PrivateChatModules.js';
import { AudioEngine } from './audio/AudioEngine.js';
import { MobileRoom } from './mobile/MobileRoom.js';
import { InviteFriendsDialog } from '../social/InviteFriendsDialog.js';
import { openDm, useCanDm } from '../social/dm.js';
import { useConfirmLeave } from './leave.js';
import { useMobile } from '../ui/mobile.js';
import { usePendingFileUse } from '../cloud/inRoom.js';
import { Desk, useTabChoices } from './Desk.js';
import './room.css';

type Update = (fn: (l: DesktopLayout) => DesktopLayout) => void;

/**
 * A partir dessa largura de janela, o espaço vazio do arranjo inicial nasce à esquerda do chat;
 * abaixo, em cima dele, onde lado a lado os dois ficariam apertados (`nextToChats` em
 * `room/layout.ts`).
 */
const WIDE_DESKTOP = 1600;
const wideDesktop = () => window.innerWidth >= WIDE_DESKTOP;

/** a janela própria que recarrega diz `here` de novo; só depois disso o que ela tem volta para a mesa */
const RELOAD_GRACE_MS = 2500;
/** a mesa recarregou: as janelas próprias abertas respondem nesse tempo (as outras voltam para a mesa) */
const HELLO_WAIT_MS = 1500;
/** a janela nova (Abas → Nova janela), larga o bastante para pôr espaços lado a lado */
const NEW_WINDOW = { left: 80, top: 80, width: 960, height: 640 };

export function RoomDesktop() {
  const t = useT();
  // o "Usar na mesa" que veio do Desk Cloud: o chat, a música e a cena nova (docs/spec/desk-cloud.md)
  usePendingFileUse();
  const room = useApp((s) => s.room);
  const confirmLeave = useConfirmLeave();
  const roomId = useApp((s) => s.activeRoomId);
  const me = useApp((s) => s.user);
  const myModes = useApp((s) => (s.user ? s.members[s.user.id]?.modes : undefined));
  const status = useApp((s) => s.status);
  const canAdminister = !!room && !!me && (room.ownerId === me.id || !!myModes?.includes('gm'));
  const voice = useVoiceChat(); // vive aqui para sobreviver à troca de módulos
  // a webcam de cada um na conferência, para a lista de Participantes mostrar no lugar da foto (BRAZ-20)
  const peerMedia = useApp((s) => s.peerMedia);
  const videos = useMemo(
    () => videoOf(voice, peerMedia, me?.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [voice.inCall, voice.camOn, voice.localStream, voice.remoteStreams, peerMedia, me?.id],
  );
  const [layout, setLayout] = useState<DesktopLayout | null>(null);
  // o menu da mesa, no ícone dela na barra da esquerda (docs/spec/room-menus.md): a lista é a da
  // última renderização; no celular, não há (a mesa tem o "Mais")
  const roomMenu = useRef<() => MenuEntry[] | null>(() => null);
  useEffect(() => (roomId ? provideRoomMenu(roomId, () => roomMenu.current()) : undefined), [roomId]);
  const [editing, setEditing] = useState(false);
  const [newGroup, setNewGroup] = useState(false);
  // "Convidar amigos" (docs/spec/friends.md): só com um servidor que entrega convites
  const [inviting, setInviting] = useState(false);
  const canInvite = useApp((s) => s.serverFeatures.presence);
  // "Falar com o mestre": a conversa direta com o dono da mesa (docs/spec/friends.md)
  const canDm = useCanDm();
  const users = useApp((s) => s.users);
  const createGroup = useApp((s) => s.createGroup);
  const openChannel = useApp((s) => s.openChannel);
  const groups = useApp((s) => s.groups);
  // no celular, a mesa é outra forma de mostrar o mesmo layout (docs/spec/mobile.md)
  const mobile = useMobile();
  const { tabChoices, tableIds, tableLabel, iconOf } = useTabChoices();

  useEffect(() => {
    if (roomId) setLayout(loadLayout(roomId, wideDesktop()));
  }, [roomId]);

  // o último espaço usado (docs/spec/room-layout.md, "Para onde vai uma aba nova"): os espaços em
  // que a pessoa clicou, na mesa e nas janelas próprias, do mais recente para o mais antigo
  const recent = useRef<string[]>([]);
  useEffect(() => {
    recent.current = [];
  }, [roomId]);
  const touch = useCallback((id: string) => {
    recent.current = [id, ...recent.current.filter((x) => x !== id)].slice(0, 16);
  }, []);
  /** Como abre a aba que a pessoa pediu (`activate`) ou que chegou sozinha; `within`: a janela própria de onde veio o pedido. */
  const openOpts = (activate: boolean, detached = false, within?: string): OpenOpts => ({ activate, detached, recent: recent.current, ...(within ? { within } : {}) });

  const update = useCallback<Update>(
    (fn) => {
      setLayout((current) => {
        if (!current || !roomId) return current;
        const next = fn(current);
        saveLayout(roomId, next);
        return next;
      });
    },
    [roomId],
  );
  const apply = useCallback((op: LayoutOp) => update((l) => applyLayoutOp(l, op)), [update]);

  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  // O canal com as janelas próprias (docs/spec/popout-windows.md): o que ela tem volta para a mesa
  // quando a janela fecha; o que a janela muda no layout e o que ela pede (abrir uma ficha, uma
  // conversa) acontece aqui; ao carregar, o que está numa janela sem ninguém volta.
  const backTimers = useRef(new Map<string, number>());
  useEffect(() => {
    if (!roomId) return;
    const timers = backTimers.current;
    const cancel = (id: string) => {
      window.clearTimeout(timers.get(id));
      timers.delete(id);
    };
    const back = (id: string) => {
      cancel(id);
      update((l) => attachWindow(l, id));
    };
    const alive = new Set<string>();
    const later = new Set<number>();
    const off = onWindowMessage((m) => {
      if (m.t === 'focus' || m.t === 'open-dm' || m.roomId !== roomId) return;
      if (m.t === 'here') {
        alive.add(m.windowId);
        cancel(m.windowId);
        // a janela que o layout não tem (o que ela tinha já voltou para a mesa): espera um pouco,
        // porque a janela aberta por outra chega antes da operação que a cria, e manda fechar
        if (!layoutRef.current || windowRoot(layoutRef.current, m.windowId)) return;
        const id = window.setTimeout(() => {
          later.delete(id);
          const l = layoutRef.current;
          if (l && !windowRoot(l, m.windowId)) postWindows({ t: 'close', roomId, windowId: m.windowId });
        }, HELLO_WAIT_MS);
        later.add(id);
      } else if (m.t === 'closed') {
        // fechou de vez, ou só recarregou (aí diz `here` de novo logo em seguida)
        cancel(m.windowId);
        if (popoutClosed(m.windowId) === true) back(m.windowId);
        else timers.set(m.windowId, window.setTimeout(() => back(m.windowId), RELOAD_GRACE_MS));
      } else if (m.t === 'layout') apply(m.op);
      else if (m.t === 'touch') touch(m.nodeId);
      else if (m.t === 'open-item') useApp.getState().requestOpenSheet(m.itemId, { within: m.windowId, ...(m.popWindow ? { popWindow: m.popWindow } : {}) });
      else if (m.t === 'open-channel') useApp.getState().openPrivate(m.channel, true, m.windowId);
    });
    postWindows({ t: 'hello', roomId });
    const reconcile = window.setTimeout(() => {
      update((l) => windowIds(l).filter((id) => !alive.has(id) && popoutClosed(id) !== false).reduce((acc, id) => attachWindow(acc, id), l));
    }, HELLO_WAIT_MS);
    // reserva: janela que esta mesa abriu e que fechou sem avisar
    const poll = window.setInterval(() => closedPopouts().forEach(back), 1000);
    return () => {
      off();
      window.clearTimeout(reconcile);
      window.clearInterval(poll);
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
      later.forEach((id) => window.clearTimeout(id));
    };
  }, [roomId, update, apply, touch]);

  // saí da mesa (ou troquei de mesa, ou saí do RPG Desk): as janelas próprias dela fecham
  useEffect(() => {
    if (!roomId) return;
    return () => postWindows({ t: 'close', roomId });
  }, [roomId]);

  // a janela que sumiu do layout (o último espaço dela fechou, ou saiu dela, ou o arranjo inicial):
  // ela fecha
  const outIds = useRef(new Set<string>());
  useEffect(() => {
    if (!layout || !roomId) return;
    const present = new Set(windowIds(layout));
    for (const id of outIds.current) if (!present.has(id)) postWindows({ t: 'close', roomId, windowId: id });
    outIds.current = present;
  }, [layout, roomId]);

  // conversa privada pedida (menu ou mensagem recebida): abre a aba junto das conversas, ou, pedida
  // de dentro de uma janela própria, nela
  useEffect(() => {
    if (!openChannel || !layout) return;
    const { channel, focus, within } = openChannel;
    const classId: ModuleClassId = channel.kind === 'pvt' ? 'system/pvtChat' : 'system/groupPVTChat';
    const param = channel.kind === 'pvt' ? channel.to : channel.groupId;
    // a conversa está numa janela própria: pedida por alguém, a janela vem para a frente, com a
    // conversa à vista
    const open = findSheet(layout, classId, param);
    const win = open ? windowOf(layout, open.sheetId) : null;
    if (open && win) {
      if (!focus) return;
      update((l) => activateSheet(l, open.nodeId, open.sheetId));
      focusPopout(win);
      return;
    }
    const opts = openOpts(focus, false, within);
    update((l) => openTab(l, classId, param, opts));
    // a conversa nova pode cair num espaço de uma janela própria
    const dest = open ? null : tabDestination(layout, classId, opts);
    const destWin = dest ? windowOfNode(layout, dest) : null;
    if (focus && destWin) focusPopout(destWin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openChannel?.nonce, layout === null]);

  // ficha (ou cena) pedida pela biblioteca: abre (ou põe à vista) a aba dela, junto das do mesmo
  // tipo, num espaço vazio ou no último espaço usado (`tabDestination`); pedida de dentro de uma
  // janela própria, nela
  const openSheet = useApp((s) => s.openSheet);
  useEffect(() => {
    if (!openSheet || !layout || !roomId) return;
    const { itemId, within, popWindow } = openSheet;
    const classId = itemModule(useApp.getState().library.find((i) => i.id === itemId)?.kind);
    const opts = openOpts(true, !!openSheet.window, within);
    const openItem = (l: DesktopLayout): DesktopLayout => openTab(l, classId, itemId, opts);
    const existing = findSheet(layout, classId, itemId);
    // já está numa janela própria: a janela vem para a frente, com a aba à vista (e a que outra
    // janela já abriu para ela fecha)
    const win = existing ? windowOf(layout, existing.sheetId) : null;
    if (existing && win) {
      if (popWindow) postWindows({ t: 'close', roomId, windowId: popWindow });
      update((l) => activateSheet(l, existing.nodeId, existing.sheetId));
      focusPopout(win);
      return;
    }
    if (!openSheet.window) {
      update(openItem);
      // a aba nova vai para o espaço de destino, que pode estar numa janela própria
      const dest = existing ? null : tabDestination(layout, classId, opts);
      const destWin = dest ? windowOfNode(layout, dest) : null;
      if (destWin) focusPopout(destWin);
      return;
    }
    // "abrir em nova janela": a janela abre já (ainda no clique; ou já abriu, pela janela própria
    // que pediu), e a aba vai para ela
    const id = popWindow ?? newLayoutId('w');
    const card = existing ? document.querySelector(`[data-node="${existing.nodeId}"]`) : null;
    if (!popWindow && !openPopout(id, roomId, card?.getBoundingClientRect() ?? null)) {
      toast(t('room.popout.blocked'), 'error');
      return;
    }
    update((l) => {
      const next = openItem(l);
      const found = findSheet(next, classId, itemId);
      return found ? popTab(next, found.sheetId, id) : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSheet?.nonce]);

  // tabelas da sala (TablesDock): cada tabela de plugin ativada abre para todos como uma aba que
  // não fica à vista, junto das outras tabelas ou no último espaço usado; desativada, a aba fecha.
  // Quem fechou a aba não a vê reabrir nesta sessão. As que vêm com o RPG Desk (o tracker, as
  // macros) estão sempre na mesa e abrem pelo menu Abas, como as outras abas
  // (docs/spec/room-menus.md).
  const dockKey = tableIds.join(',');
  const autoDockKey = useApp((s) =>
    s.library
      .filter((i) => i.kind === 'dock' && !i.inactive && !isBuiltinTable(i.dataType))
      .map((i) => i.id)
      .join(','),
  );
  const seenDocks = useRef({ roomId: '', ids: new Set<string>() });
  useEffect(() => {
    if (!layout || !room) return;
    if (seenDocks.current.roomId !== room.id) seenDocks.current = { roomId: room.id, ids: new Set() };
    const seen = seenDocks.current.ids;
    const ids = dockKey ? dockKey.split(',') : [];
    const auto = autoDockKey ? autoDockKey.split(',') : [];
    // desativada e reativada: abre de novo para todos
    for (const id of [...seen]) if (!ids.includes(id)) seen.delete(id);
    update((l) => {
      let next = l;
      for (const site of Object.values(l.sites)) {
        (function walk(n: DockNode) {
          if (n.kind === 'content') {
            for (const sh of n.sheets) if (sh.classId === 'system/tableDock' && sh.param && !ids.includes(sh.param)) next = closeParamSheets(next, 'system/tableDock', sh.param);
          } else n.children.forEach(walk);
        })(site!.node);
      }
      for (const id of auto) {
        if (seen.has(id)) continue;
        seen.add(id);
        next = openTab(next, 'system/tableDock', id, openOpts(false));
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dockKey, autoDockKey, room?.id, layout === null]);

  // saí de um grupo (ou ele sumiu): fecha a aba dele
  useEffect(() => {
    if (!layout) return;
    const stale = new Set<string>();
    for (const site of Object.values(layout.sites)) {
      (function walk(n: DockNode) {
        if (n.kind === 'content') {
          for (const sh of n.sheets) if (sh.classId === 'system/groupPVTChat' && sh.param && !groups[sh.param]) stale.add(sh.param);
        } else n.children.forEach(walk);
      })(site!.node);
    }
    if (stale.size) update((l) => [...stale].reduce((acc, id) => closeParamSheets(acc, 'system/groupPVTChat', id), l));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, room?.id]);

  // a mesa desligou o registro das sessões (docs/spec/session-clock.md, "Ligar e desligar"): a aba
  // Sessões fecha, na mesa e nas janelas próprias
  const record = sessionRules(room).record;
  useEffect(() => {
    if (layout && !record && findSheet(layout, 'system/gameSessions')) update((l) => withoutModule(l, 'system/gameSessions'));
  }, [layout, record, update]);

  // uma aba aberta pelos menus: no espaço que `tabDestination` escolhe; a que está numa janela
  // própria (ou cai num espaço de uma janela) vem para a frente. `avoidChat`: a aba pedida pelo
  // relógio não cobre o chat da mesa, se houver outro espaço
  const showPanel = (classId: ModuleClassId, param?: string, avoidChat = false) => {
    if (!layout || (classId === 'system/gameSessions' && !record)) return;
    const existing = findTab(layout, classId, param);
    const win = existing ? windowOf(layout, existing.sheetId) : null;
    if (win) {
      focusPopout(win);
      return;
    }
    const chat = avoidChat ? chatPanel(layout) : null;
    const opts: OpenOpts = { ...openOpts(true), ...(chat ? { avoid: chat.id } : {}) };
    update((l) => openTab(l, classId, param, opts));
    const dest = existing ? null : tabDestination(layout, classId, opts);
    const destWin = dest ? windowOfNode(layout, dest) : null;
    if (destWin) focusPopout(destWin);
  };

  // uma aba pedida de fora dos menus: o relógio da sessão abre a aba Sessões (no celular, quem abre
  // é a página Mais, em MobileRoom)
  const openModule = useApp((s) => s.openModule);
  useEffect(() => {
    if (!openModule || !layout || mobile) return;
    showPanel(openModule.classId, undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModule?.nonce]);

  // levar a uma sessão (o ★ do relógio, o Ver de um aviso; docs/spec/session-clock.md, "Avaliações"):
  // o pedido pode vir de outra mesa ou de outra tela, então a aba Sessões só abre quando a mesa dele
  // está pronta, uma vez por pedido. A aba abre o cartão da sessão
  const sessionFocus = useApp((s) => s.sessionFocus);
  const settleFocus = useApp((s) => s.settleSessionFocus);
  const ready = !!layout && room?.id === roomId;
  useEffect(() => {
    if (!sessionFocus || sessionFocus.opened || sessionFocus.roomId !== roomId || !ready || mobile) return;
    showPanel('system/gameSessions', undefined, true);
    settleFocus(sessionFocus.nonce, 'opened');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionFocus, roomId, ready, mobile]);

  if (!room || !layout || !roomId) {
    return (
      <div className="rd-room rd-room--loading">
        <span className="rd-spinner" /> {t('room.entering')}
      </div>
    );
  }

  // as de plugin: as que voltam como abas no arranjo inicial
  const autoTableIds = autoDockKey ? autoDockKey.split(',') : [];

  // o que se desenha: sem as janelas próprias
  const shown = visibleLayout(layout);
  const groupDialog = newGroup ? (
    <ChooseUsersDialog
      title={t('pvt.group.new.title')}
      action={t('pvt.group.new.action')}
      empty={t('pvt.group.new.empty')}
      users={users.filter((u) => u.id !== me?.id)}
      onClose={() => setNewGroup(false)}
      onChoose={createGroup}
    />
  ) : null;
  const inviteDialog = inviting ? <InviteFriendsDialog onClose={() => setInviting(false)} /> : null;
  if (mobile) {
    roomMenu.current = () => null;
    return (
      <VideoContext.Provider value={videos}>
        <MobileRoom layout={shown} update={update} voice={voice} canAdminister={canAdminister} onNewGroup={() => setNewGroup(true)} onInvite={canInvite ? () => setInviting(true) : undefined} />
        <AudioEngine />
        <ConferenceAudio voice={voice} />
        {groupDialog}
        {inviteDialog}
      </VideoContext.Provider>
    );
  }

  const resetAll = async () => {
    const ok = await askConfirm({ title: t('room.layout.reset'), message: t('room.layout.reset.message'), action: t('room.layout.reset.action'), danger: true });
    if (ok) update((l) => resetLayout(l, autoTableIds, wideDesktop()));
  };
  // uma janela própria nova, vazia (docs/spec/popout-windows.md): ela abre ainda no clique
  const openWindow = () => {
    const id = newLayoutId('w');
    if (!openPopout(id, roomId, NEW_WINDOW)) toast(t('room.popout.blocked'), 'error');
    else update((l) => newWindow(l, id, wideDesktop()));
  };

  // o menu Abas: ✓ no que está à vista. Sem ✓, o clique abre (ou mostra a aba, e o espaço
  // recolhido); com ✓, fecha a aba. O que está numa janela própria tem ✓, e o clique traz a janela
  // para a frente. O chat da mesa não fecha: à vista, o item fica apagado. No fim, organizar a tela
  // e abrir uma janela nova.
  const tabEntry = (classId: ModuleClassId, label: string, param?: string): MenuEntry => {
    const found = findTab(layout, classId, param);
    const win = found ? windowOf(layout, found.sheetId) : null;
    const out = !!win;
    const inView = !!found && (out || isActiveSheet(shown, found.sheetId));
    const fixed = classId === 'system/mainChat' && inView;
    return {
      label,
      toggle: true,
      checked: inView,
      ...iconOf(classId),
      ...(fixed ? { disabled: true, hint: t('room.group.chats.fixed') } : out ? { hint: t('room.panels.out') } : {}),
      onSelect: () => {
        if (!found) showPanel(classId, param);
        else if (win) focusPopout(win);
        else if (inView) update((l) => closeSheet(l, found.sheetId));
        else update((l) => activateSheet(l, found.nodeId, found.sheetId));
      },
    };
  };
  const tabEntries: MenuEntry[] = [
    ...tabChoices.filter((c) => c.classId !== 'system/roomSettings').map((c) => tabEntry(c.classId, c.label)),
    ...tableIds.map((id) => tabEntry('system/tableDock', tableLabel(id), id)),
    { kind: 'separator' },
    { label: editing ? t('room.layout.done') : t('room.layout.edit'), shortcut: 'Ctrl+E', icon: <Icon name="grid" size={16} />, onSelect: () => setEditing((v) => !v) },
    { label: t('room.layout.reset'), onSelect: () => void resetAll() },
    { label: t('room.window.new'), icon: <Icon name="popout" size={16} />, onSelect: openWindow },
  ];
  // o menu da mesa (docs/spec/room-menus.md): quem não administra só vê os plugins
  const roomEntries: MenuEntry[] = [
    ...(canInvite ? [{ label: t('room.inviteFriends.menu'), icon: <Icon name="userPlus" size={16} />, onSelect: () => setInviting(true) }] : []),
    ...(canDm && room && me && room.ownerId !== me.id
      ? [{ label: t('room.talkToGm'), icon: <Icon name="mail" size={16} />, onSelect: () => openDm(room.ownerId, users.find((u) => u.id === room.ownerId)) }]
      : []),
    { label: t(canAdminister ? 'room.settings' : 'room.settings.plugins'), icon: <Icon name="settings" size={16} />, onSelect: () => showPanel('system/roomSettings') },
    { kind: 'submenu', label: t('room.tabs'), icon: <Icon name="list" size={16} />, hint: t('room.tabs.hint'), entries: tabEntries },
    { kind: 'separator' },
    { label: t('room.leave'), icon: <Icon name="logout" size={16} />, danger: true, onSelect: () => void confirmLeave() },
  ];
  roomMenu.current = () => roomEntries;

  return (
    <VideoContext.Provider value={videos}>
      <VoiceContext.Provider value={voice}>
        <Desk layout={layout} shown={shown} win={null} roomId={roomId} apply={apply} editing={editing} onEditing={setEditing} onTouch={touch} onReset={() => void resetAll()}>
          {status !== 'online' ? (
            <div className="rd-room__reconnecting" role="status">
              <span className="rd-room__reconnectingDot" /> {t('room.status.connecting')}
            </div>
          ) : null}
          <AudioEngine />
          <ConferenceAudio voice={voice} />
          {groupDialog}
          {inviteDialog}
        </Desk>
      </VoiceContext.Provider>
    </VideoContext.Provider>
  );
}
